import { ComponentSelectors, ComponentSelector, ComponentElements, UIComponent, ComponentInjector, ComponentObject, ComponentService, Inject, Injectable} from '@atypon/ui-utils';
import { VPortListener } from "ui-core/plugins/enquireJs/js/helper";

class Texts extends ComponentObject {}

class ClassList extends ComponentObject {}

class Selectors extends ComponentSelectors {
    navSticky = new ComponentSelector('.pages-nav--sticky', "document");

    navRes = new ComponentSelector('.pages-nav--res', ".pages-nav--sticky");
    pageHeader = new ComponentSelector('.pageHeader', "document");
    hubpageMenuNav = new ComponentSelector('.hubpage-menu__nav > ul', "document");
    menuItems = new ComponentSelector('.hubpage-menu__nav .menu-parent', "document");
    dropdownToggles = new ComponentSelector('.dropdown__toggle', "document");
    dropdownMenus = new ComponentSelector('.dropdown__menu', "document");
    hubpageMenu = new ComponentSelector('.hubpage-menu', "document");
    wSlide = new ComponentSelector('.w-slide', "document");
    wSlideBackBtn = '.w-slide__back';
    hubMenuTarget = '[data-db-target-for="hub-menu"]';
    coolBarRes = '.coolBar--res [data-db-target-for]';
    resultFilters = '.doSearch [data-db-target-for].result__ctrl__filters';
    fixedSwitch = '.js--fixed-switch';
    uxModalContainer = '.ux-modal-container';
    modalDialog = '.modal__dialog';
}

class Elements extends ComponentElements<Selectors> {
    navSticky: HTMLElement;
    navRes: HTMLElement;
    pageHeader: HTMLElement;
    hubpageMenuNav: HTMLElement;
    menuItems: HTMLElement[] = [];
    dropdownToggles: HTMLElement[] = [];
    dropdownMenus: HTMLElement[] = [];
    hubpageMenu: HTMLElement;
    wSlide: HTMLElement;
}

interface Navigation extends ComponentService<Selectors, Elements, ClassList, Texts> {}

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class Navigation implements UIComponent {
    
    @VPortListener('md')
    isTablet = false;
   
    constructor(readonly wrapper: HTMLElement) { }

    initialize(): void {
        this.elements.initialize(this.wrapper);
        this.addEventListeners();
        this.updateAccessibilityAttributes();
    }

    isTabletOn(): void {
        this.isTablet = true;
    }

    isTabletOff(): void {
        this.isTablet = false;
    }
    
    addEventListeners(): void {
        window.addEventListener('load',()=> {this.handlePageLoad()});
        document.body.addEventListener('click', (event)=>{this.handleBodyClick(event)});
        document.addEventListener('click',(event)=>{this.handleModalToggle(event)});
    }

    private handlePageLoad(): void {
        this.updateNavStickyPadding();
        this.setupHubMenuEvents();
    }

    private updateNavStickyPadding(): void {
        const { navSticky, navRes, pageHeader } = this.elements;
        if (navSticky && navRes && pageHeader) {
            navSticky.style.top = `${pageHeader.offsetHeight}px`;
        }
    }

    private handleTabletView = (): void => {
        
        const {hubpageMenu} = this.elements;
        
        if (this.domUtils.getElements(this.selectors.fixedSwitch).length > 0) {
            this.domUtils.getElements(this.selectors.fixedSwitch).forEach(el => el.classList.remove('fixed-element'));
        }

        if (hubpageMenu) {
            const dropdownToggles = this.domUtils.getElements(this.selectors.dropdownToggles.selector, hubpageMenu);
            dropdownToggles.forEach(toggle => {
                if (toggle.classList.contains('js--open')) {
                    toggle.classList.remove('js--open', 'hover');
                    toggle.setAttribute('aria-expanded', 'false');
                }
            });
        }

        this.updateNavStickyPadding();
    };
    
    private handleDesktopView = (): void => {

        const {hubpageMenuNav, wSlide} = this.elements;

        if (this.domUtils.getElements(this.selectors.fixedSwitch).length > 0) {
            this.domUtils.getElements(this.selectors.fixedSwitch).forEach(el => el.classList.add('fixed-element'));
        }

        if (hubpageMenuNav && hubpageMenuNav.classList.contains('js--open')) {
            hubpageMenuNav.classList.remove('js--open');
            const hubpageMenuBurger = this.domUtils.getElement(this.selectors.hubMenuTarget);
            if (hubpageMenuBurger) {
                hubpageMenuBurger.classList.remove('js--open');
                hubpageMenuBurger.setAttribute('aria-expanded', 'false');
                const icon = hubpageMenuBurger.querySelector('i');
                icon?.classList.remove('icon-close_thin');
            }
        }

        if (wSlide) {

            const menuItemsContainer = wSlide.querySelector('.menu-items-container');
            const originalTarget = wSlide.getAttribute('original-target') as string;
            const originalTargetMenu = this.domUtils.getElement(originalTarget);
            
            if ( menuItemsContainer && originalTarget && originalTargetMenu ) {
                originalTargetMenu.innerHTML = '';
                originalTargetMenu.append(menuItemsContainer);
                wSlide.removeAttribute('original-target');
            }
        }
    };

    private setupHubMenuEvents(): void {
        const { hubpageMenu, hubpageMenuNav} = this.elements;
        const hubMenuTarget = this.domUtils.getElement(this.selectors.hubMenuTarget);

        hubMenuTarget?.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            const isOpen = target.classList.contains('js--open');
            const menuSpan = target.querySelector('span');

            if (menuSpan) {
                const pageHeaderHeight = this.elements.pageHeader?.offsetHeight || 0;
                menuSpan.style.top = `${pageHeaderHeight - target.getBoundingClientRect().top - 20}px`;
            }

            if (isOpen && hubpageMenu) {
                const pageHeaderHeight = this.elements.pageHeader?.offsetHeight || 0;
                hubpageMenu.style.top = `${pageHeaderHeight}px`;
                document.body.classList.add('lock-screen');
                document.documentElement.classList.add('lock-screen');
                this.elements.wSlide?.classList.add('hubMenu-slide');
                this.elements.wSlide!.style.top = `${pageHeaderHeight}px`;
            } else {
                this.closeHubMenu();
            }
        });

        hubpageMenuNav?.setAttribute('role', 'menubar');
        
        this.isTablet ? this.handleTabletView(): this.handleDesktopView();
    }
        
    private closeHubMenu(): void {
        this.toggleScreenLock(false);
        this.elements.wSlide?.classList.remove('hubMenu-slide');
    }

    private toggleScreenLock(isLocked: boolean): void { 
        document.body.classList.toggle('lock-screen', isLocked);
        document.documentElement.classList.toggle('lock-screen', isLocked);
    }

    private handleBodyClick(event: Event): void {
        const target = event.target as HTMLElement;
        const matchesSelector = target.matches(`${this.selectors.coolBarRes}, ${this.selectors.resultFilters}`);
        if (matchesSelector) {
            if (target.classList.contains('js--open')) {
                this.toggleScreenLock(true);
            } else {
                this.toggleScreenLock(false);
            }
        }
    }

    private handleModalToggle(event: Event): void {
        const target = event.target as HTMLElement;
        if (target.matches('[data-toggle="modal"]') || target.matches('[data-dismiss="modal"]')) {
            const hubpageMenu = this.elements.hubpageMenu;
            const hubpageMenuNav = hubpageMenu?.querySelector('nav');
            if (hubpageMenuNav) {
                hubpageMenuNav.style.zIndex = target.matches('[data-toggle="modal"]') ? '8' : '11';
            }
        }

        if(target.matches(this.selectors.wSlideBackBtn)){
            const {getElement} = this.domUtils;

           const hubMenu= getElement(this.selectors.hubMenuTarget)
           const focusableHubElements= getElement('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])', hubMenu);
           const currentActiveElement= document.activeElement as HTMLElement;

           focusableHubElements?.focus();
           currentActiveElement?.blur();

            UX.dropBlock.on.show(hubMenu);
        }
    }

    private updateAccessibilityAttributes(): void {

        const { hubpageMenuNav, menuItems } = this.elements;
        const hubpageMenuListItem= this.domUtils.getElements('li', hubpageMenuNav);
        
        hubpageMenuNav?.setAttribute('role', 'menubar');

        hubpageMenuListItem.forEach((listItem) => {
            if (!listItem.classList.value) {
                listItem.classList.add('menu-parent');
                listItem.setAttribute('role', 'presentation');
                const emptyLinkItem = this.domUtils.getElement('a', listItem);
                emptyLinkItem?.classList.add('w-slide__btn');
                emptyLinkItem?.setAttribute('role', 'menuitem');
            }
        });

        menuItems?.forEach(menuItem => {
            const menuToggle = this.domUtils.getElement('.dropdown__toggle', menuItem),
                submenu = this.domUtils.getElement('.dropdown__menu', menuItem),
                menuLinks = this.domUtils.getElements('a', submenu);
    
            menuItem.setAttribute('role', 'presentation');
            menuToggle?.setAttribute('aria-haspopup', 'true');
            menuToggle?.setAttribute('aria-expanded', 'false');
            menuToggle?.setAttribute('role', 'menuitem');
            submenu?.setAttribute('role', 'menu');
    
            menuLinks?.forEach(link => {
                link.setAttribute('role', 'menuitem');
                link.closest('li')?.setAttribute('role', 'presentation');
            });
        });
    }
}

export default Navigation;

export {
    Texts as NavigationTexts,
    ClassList as NavigationClassList,
    Selectors as NavigationSelectors,
    Elements as NavigationElements,
};
